import { useCallback } from 'react';
import html2canvas from 'html2canvas';

export const useCreativeDownload = () => {
	const downloadHtmlAsImage = useCallback(
		async (htmlContent: string, width: number, height: number, filename: string, shouldDownload = true): Promise<void | Blob> => {
			const iframe = document.createElement('iframe');
			iframe.style.width = `${width}px`;
			iframe.style.height = `${height}px`;
			iframe.style.position = 'fixed';
			iframe.style.left = '-9999px';
			document.body.appendChild(iframe);

			try {
				await new Promise<void>(resolve => {
					iframe.onload = () => resolve();
					iframe.srcdoc = htmlContent;
				});

				const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
				if (!iframeDocument) {
					throw new Error('Failed to access iframe document');
				}

				// Wait for fonts to load
				if (iframeDocument.fonts) {
					await Promise.race([iframeDocument.fonts.ready, new Promise(resolve => setTimeout(resolve, 2000))]);
				} else {
					await new Promise(resolve => setTimeout(resolve, 1000));
				}

				// Additional wait to ensure rendering is complete
				await new Promise(resolve => setTimeout(resolve, 300));

				const canvas = await html2canvas(iframeDocument.body, {
					width,
					height,
					scale: 1,
					backgroundColor: '#ffffff',
					logging: false,
					useCORS: true,
					allowTaint: true,
					foreignObjectRendering: true,
					windowWidth: width,
					windowHeight: height,
				});

				return new Promise<void | Blob>((resolve, reject) => {
					canvas.toBlob(blob => {
						if (blob) {
							if (shouldDownload) {
								const link = document.createElement('a');
								link.href = URL.createObjectURL(blob);
								link.download = filename;
								link.click();
								URL.revokeObjectURL(link.href);
								resolve();
							} else {
								resolve(blob);
							}
						} else {
							reject(new Error('Failed to convert canvas to blob'));
						}
					}, 'image/png');
				});
			} catch (error) {
				console.error('Failed to convert HTML to image:', error);
				throw error;
			} finally {
				document.body.removeChild(iframe);
			}
		},
		[]
	);

	const download = useCallback(
		async (
			resourceUrl: string,
			width: number,
			height: number,
			filename = 'creative.png',
			shouldDownload = true,
			bannerHtmlText?: string,
			downloadAsImage = false
		): Promise<void | Blob> => {
			const isHtmlFile = resourceUrl.endsWith('.html');

			if (isHtmlFile) {
				try {
					const response = await fetch(resourceUrl);
					const html = bannerHtmlText || (await response.text());

					if (downloadAsImage && width > 0 && height > 0) {
						return await downloadHtmlAsImage(html, width, height, filename, shouldDownload);
					}

					const blob = new Blob([html], { type: 'text/html' });

					if (shouldDownload) {
						const link = document.createElement('a');
						link.href = URL.createObjectURL(blob);
						link.download = filename.replace(/\.(png|jpg|jpeg)$/i, '.html');
						link.click();
						URL.revokeObjectURL(link.href);
						return;
					} else {
						return blob;
					}
				} catch (error) {
					console.error('Failed to download HTML:', error);
					throw error;
				}
			} else {
				return new Promise<void | Blob>((resolve, reject) => {
					const image = new Image();
					image.crossOrigin = 'anonymous';
					image.src = resourceUrl;

					image.onload = () => {
						const originalWidth = image.width;
						const originalHeight = image.height;
						const imageAspectRatio = originalWidth / originalHeight;
						const canvasAspectRatio = width / height;

						let targetWidth = width;
						let targetHeight = height;

						if (imageAspectRatio > canvasAspectRatio) {
							// Image is wider: fit by width
							targetWidth = width;
							targetHeight = width / imageAspectRatio;
						} else {
							// Image is taller: fit by height
							targetHeight = height;
							targetWidth = height * imageAspectRatio;
						}

						const offsetX = (width - targetWidth) / 2;
						const offsetY = 0;

						const canvas = document.createElement('canvas');
						canvas.width = width;
						canvas.height = height;

						const ctx = canvas.getContext('2d');
						if (!ctx) {
							reject(new Error('Failed to get canvas context'));
							return;
						}

						// Fill background with white
						ctx.fillStyle = 'white';
						ctx.fillRect(0, 0, width, height);

						// Draw image centered
						ctx.drawImage(image, offsetX, offsetY, targetWidth, targetHeight);

						canvas.toBlob(blob => {
							if (!blob) {
								reject(new Error('Failed to convert canvas to blob'));
								return;
							}

							if (shouldDownload) {
								const link = document.createElement('a');
								link.href = URL.createObjectURL(blob);
								link.download = filename;
								link.click();

								URL.revokeObjectURL(link.href);

								resolve(); // Resolve after triggering the download
							} else {
								resolve(blob); // Resolve with Blob when not downloading
							}
						}, 'image/png');
					};

					image.onerror = () => {
						reject(new Error('Failed to load image'));
					};
				});
			}
		},
		[]
	);

	return download;
};
